import React, { useState, useEffect, useMemo } from "react";
import Sidebar from "../../component/Sidebar";
import Topbar from "../../component/Topbar";
import {
  FiPlus, FiTag, FiEdit2, FiLayers, FiChevronsUp, FiChevronsDown,
  FiChevronLeft, FiChevronRight, FiActivity, FiEye, FiX,
  FiFileText, FiCalendar, FiUser, FiSearch, FiDollarSign,
} from "react-icons/fi";
import axios from "axios";
import { baseurl, imgurl } from "../../baseurl";

/* ─── CSS ─── */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=DM+Sans:wght@400;500;600&display=swap');

:root {
  --ec-teal:      #0a5556;
  --ec-teal-mid:  #0c7172;
  --ec-teal-lite: #14b8a6;
  --ec-gold:      #FFD166;
  --ec-bg:        #eef6f6;
  --ec-white:     #ffffff;
  --ec-border:    rgba(10,85,86,0.10);
  --ec-text:      #0f2a2a;
  --ec-muted:     #5a8080;
  --ec-danger:    #ef4444;
  --ec-success:   #10b981;
  --ec-warning:   #f59e0b;
}

@keyframes ec__fadeUp  { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
@keyframes ec__shimmer { 0%{background-position:-600px 0} 100%{background-position:600px 0} }
@keyframes ec__spin    { to{transform:rotate(360deg)} }
@keyframes ec__modalIn { from{opacity:0;transform:scale(0.96) translateY(10px)} to{opacity:1;transform:scale(1) translateY(0)} }

.ec__wrap {
  font-family:'DM Sans',sans-serif;
  padding:22px 22px 40px;
  background:var(--ec-bg);
  min-height:100vh;
  animation:ec__fadeUp 0.4s ease both;
}

/* ── header ── */
.ec__hd { display:flex; align-items:flex-start; justify-content:space-between; gap:16px; margin-bottom:20px; flex-wrap:wrap; }
.ec__hd-left { display:flex; align-items:center; gap:14px; }
.ec__hd-icon {
  width:48px; height:48px; border-radius:14px; flex-shrink:0;
  background:linear-gradient(135deg,var(--ec-teal),var(--ec-teal-lite));
  display:flex; align-items:center; justify-content:center;
  font-size:20px; color:#fff; box-shadow:0 6px 18px rgba(10,85,86,0.26);
}
.ec__hd-title { font-family:'Sora',sans-serif; font-size:20px; font-weight:800; color:var(--ec-text); letter-spacing:-0.03em; margin:0 0 3px; }
.ec__hd-sub   { font-size:12.5px; color:var(--ec-muted); margin:0; }

/* ── main card ── */
.ec__card {
  background:var(--ec-white); border-radius:20px;
  border:1px solid var(--ec-border);
  box-shadow:0 2px 20px rgba(10,85,86,0.07),0 8px 40px rgba(0,0,0,0.03);
  overflow:hidden; position:relative;
}
.ec__card::before {
  content:''; position:absolute; top:0; left:0; right:0; height:3px;
  background:linear-gradient(90deg,var(--ec-teal) 0%,var(--ec-teal-lite) 55%,var(--ec-gold) 100%);
}

/* ── toolbar ── */
.ec__toolbar {
  display:flex; align-items:center; justify-content:space-between;
  gap:14px; padding:18px 22px; flex-wrap:wrap;
  border-bottom:1px solid var(--ec-border);
}
.ec__toolbar-left { display:flex; align-items:center; gap:10px; }
.ec__show-lbl { font-size:12.5px; color:var(--ec-muted); white-space:nowrap; }
.ec__select {
  padding:7px 28px 7px 11px; font-size:12.5px;
  color:var(--ec-text); background:#f3fafa;
  border:1.5px solid rgba(10,85,86,0.15); border-radius:9px;
  outline:none; cursor:pointer; appearance:none;
  background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='11' height='11' viewBox='0 0 24 24' fill='none' stroke='%235a8080' stroke-width='2.5'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
  background-repeat:no-repeat; background-position:right 9px center;
  transition:border-color 0.18s, box-shadow 0.18s;
}
.ec__select:focus { border-color:var(--ec-teal); box-shadow:0 0 0 3px rgba(10,85,86,0.09); }
.ec__search {
  display:flex; align-items:center; gap:8px;
  background:#f3fafa; border:1.5px solid rgba(10,85,86,0.15);
  border-radius:10px; padding:8px 13px;
  transition:border-color 0.18s, box-shadow 0.18s;
}
.ec__search:focus-within { border-color:var(--ec-teal); box-shadow:0 0 0 3px rgba(10,85,86,0.09); background:#fff; }
.ec__search-icon  { color:var(--ec-muted); font-size:14px; flex-shrink:0; }
.ec__search-input {
  border:none; outline:none; background:transparent;
  font-size:13px; font-family:'DM Sans',sans-serif; color:var(--ec-text); width:200px;
}
.ec__search-input::placeholder { color:#9ababa; }

/* ── table ── */
.ec__table-wrap { overflow-x:auto; }
.ec__table { width:100%; border-collapse:collapse; }
.ec__table thead th {
  font-family:'Sora',sans-serif; font-size:10.5px; font-weight:700;
  letter-spacing:0.09em; text-transform:uppercase; color:var(--ec-teal);
  background:rgba(10,85,86,0.04); padding:11px 18px;
  white-space:nowrap; border-bottom:1px solid var(--ec-border);
  cursor:pointer; user-select:none;
}
.ec__table thead th:hover { color:var(--ec-teal-lite); }
.ec__sort-icon { display:inline-flex; vertical-align:middle; margin-left:3px; opacity:.45; }
.ec__sort-icon--on { opacity:1; color:var(--ec-teal-lite); }

.ec__table tbody tr { border-bottom:1px solid rgba(10,85,86,0.055); transition:background 0.13s; }
.ec__table tbody tr:last-child { border-bottom:none; }
.ec__table tbody tr:hover { background:rgba(10,85,86,0.025); }
.ec__table tbody td { padding:13px 18px; font-size:13.5px; color:var(--ec-text); vertical-align:middle; }

/* cells */
.ec__cat-cell { display:flex; align-items:center; gap:11px; }
.ec__cat-icon {
  width:36px; height:36px; border-radius:11px; flex-shrink:0;
  background:linear-gradient(135deg,var(--ec-teal),var(--ec-teal-lite));
  display:flex; align-items:center; justify-content:center;
  font-size:15px; color:#fff; box-shadow:0 3px 10px rgba(10,85,86,0.20);
}
.ec__cat-name { font-family:'Sora',sans-serif; font-size:13px; font-weight:700; color:var(--ec-text); }

.ec__count-badge {
  display:inline-flex; align-items:center; gap:5px;
  font-size:11.5px; font-weight:700; font-family:'Sora',sans-serif;
  background:rgba(10,85,86,0.07); color:var(--ec-teal);
  border:1px solid rgba(10,85,86,0.14); border-radius:50px;
  padding:3px 11px;
}
.ec__amount { font-family:'Sora',sans-serif; font-size:14px; font-weight:800; color:var(--ec-teal); }
.ec__latest { font-size:12px; color:var(--ec-muted); }

.ec__view-btn {
  display:inline-flex; align-items:center; gap:6px;
  padding:6px 14px; border-radius:9px; border:none; cursor:pointer;
  font-size:12px; font-family:'Sora',sans-serif; font-weight:700;
  color:var(--ec-teal); background:rgba(10,85,86,0.08);
  border:1.5px solid rgba(10,85,86,0.16);
  transition:background 0.15s, box-shadow 0.15s;
}
.ec__view-btn:hover { background:rgba(10,85,86,0.14); box-shadow:0 3px 10px rgba(10,85,86,0.14); }

/* shimmer skeleton */
.ec__skel {
  border-radius:8px;
  background:linear-gradient(90deg,#e8f4f4 0%,#d0eded 50%,#e8f4f4 100%);
  background-size:600px 100%;
  animation:ec__shimmer 1.4s linear infinite;
}

/* loading spinner */
.ec__spinner {
  width:38px; height:38px; border-radius:50%;
  border:3px solid rgba(10,85,86,0.15); border-top-color:var(--ec-teal);
  animation:ec__spin 0.8s linear infinite; margin:50px auto;
}

/* empty */
.ec__empty { padding:60px 20px; text-align:center; }
.ec__empty-icon { width:56px; height:56px; border-radius:18px; margin:0 auto 14px; background:rgba(10,85,86,0.07); display:flex; align-items:center; justify-content:center; font-size:24px; }
.ec__empty-title { font-family:'Sora',sans-serif; font-size:15px; font-weight:700; color:var(--ec-text); margin-bottom:5px; }
.ec__empty-sub   { font-size:13px; color:var(--ec-muted); }

/* footer */
.ec__footer {
  display:flex; align-items:center; justify-content:space-between;
  padding:14px 22px; border-top:1px solid var(--ec-border); flex-wrap:wrap; gap:10px;
}
.ec__footer-info { font-size:12.5px; color:var(--ec-muted); }
.ec__footer-info strong { color:var(--ec-text); }

/* pagination */
.ec__pagination { display:flex; align-items:center; gap:4px; }
.ec__page-btn {
  min-width:32px; height:32px; border-radius:9px; border:1.5px solid var(--ec-border);
  background:var(--ec-white); font-size:12px; font-weight:600; color:var(--ec-muted);
  cursor:pointer; padding:0 8px; display:flex; align-items:center; justify-content:center; gap:3px;
  transition:all 0.15s;
}
.ec__page-btn:hover:not(:disabled) { border-color:var(--ec-teal); color:var(--ec-teal); background:rgba(10,85,86,0.05); }
.ec__page-btn--active { background:var(--ec-teal) !important; border-color:var(--ec-teal) !important; color:#fff !important; }
.ec__page-btn:disabled { opacity:.35; cursor:not-allowed; }

/* ── MODAL ── */
.ec__overlay {
  position:fixed; inset:0; z-index:1050;
  background:rgba(10,30,30,0.55); backdrop-filter:blur(5px);
  display:flex; align-items:center; justify-content:center; padding:20px;
}
.ec__modal {
  background:var(--ec-white); border-radius:24px; width:100%; max-width:760px;
  box-shadow:0 30px 80px rgba(10,85,86,0.22),0 10px 30px rgba(0,0,0,0.12);
  overflow:hidden; position:relative;
  animation:ec__modalIn 0.28s cubic-bezier(0.34,1.3,0.64,1) both;
}
.ec__modal::before {
  content:''; position:absolute; top:0; left:0; right:0; height:3px;
  background:linear-gradient(90deg,var(--ec-teal),var(--ec-teal-lite),var(--ec-gold));
}
.ec__modal-hd {
  display:flex; align-items:center; justify-content:space-between;
  padding:18px 24px 14px; border-bottom:1px solid var(--ec-border);
}
.ec__modal-hd-left { display:flex; align-items:center; gap:12px; }
.ec__modal-hd-icon {
  width:38px; height:38px; border-radius:11px; flex-shrink:0;
  background:linear-gradient(135deg,var(--ec-teal),var(--ec-teal-lite));
  display:flex; align-items:center; justify-content:center;
  font-size:16px; color:#fff; box-shadow:0 4px 12px rgba(10,85,86,0.26);
}
.ec__modal-title { font-family:'Sora',sans-serif; font-size:15px; font-weight:800; color:var(--ec-text); letter-spacing:-0.02em; }
.ec__modal-sub   { font-size:11.5px; color:var(--ec-muted); margin-top:1px; }
.ec__modal-close {
  width:32px; height:32px; border-radius:9px; border:none;
  background:rgba(10,85,86,0.07); cursor:pointer; color:var(--ec-muted);
  display:flex; align-items:center; justify-content:center; font-size:16px;
  transition:background 0.15s, color 0.15s;
}
.ec__modal-close:hover { background:rgba(239,68,68,0.09); color:var(--ec-danger); }

.ec__modal-body { max-height:55vh; overflow-y:auto; }
/* modal table */
.ec__modal-table { width:100%; border-collapse:collapse; }
.ec__modal-table thead th {
  font-family:'Sora',sans-serif; font-size:10px; font-weight:700;
  letter-spacing:0.09em; text-transform:uppercase; color:var(--ec-teal);
  background:rgba(10,85,86,0.04); padding:10px 18px;
  position:sticky; top:0; border-bottom:1px solid var(--ec-border);
  white-space:nowrap;
}
.ec__modal-table tbody tr { border-bottom:1px solid rgba(10,85,86,0.055); }
.ec__modal-table tbody tr:last-child { border-bottom:none; }
.ec__modal-table tbody tr:hover { background:rgba(10,85,86,0.022); }
.ec__modal-table tbody td { padding:11px 18px; font-size:13px; color:var(--ec-text); vertical-align:middle; }
.ec__row-title  { font-size:13px; font-weight:600; color:var(--ec-text); }
.ec__row-vendor { display:flex; align-items:center; gap:4px; font-size:11px; color:var(--ec-muted); margin-top:2px; }
.ec__row-date   { display:flex; align-items:center; gap:4px; font-size:12px; color:var(--ec-muted); justify-content:center; }
.ec__row-amount { font-family:'Sora',sans-serif; font-size:13.5px; font-weight:800; color:var(--ec-teal); text-align:right; }

.ec__status-badge {
  display:inline-flex; align-items:center; gap:4px;
  font-size:10.5px; font-weight:700; font-family:'Sora',sans-serif;
  border-radius:50px; padding:3px 9px;
}
.ec__status-badge--paid    { background:rgba(16,185,129,0.09); color:#047857; border:1px solid rgba(16,185,129,0.22); }
.ec__status-badge--warning { background:rgba(245,158,11,0.09); color:#b45309; border:1px solid rgba(245,158,11,0.24); }

.ec__receipt-btn {
  display:inline-flex; align-items:center; gap:4px;
  padding:4px 10px; border-radius:8px; text-decoration:none;
  font-size:11px; font-family:'Sora',sans-serif; font-weight:700;
  color:var(--ec-teal); background:rgba(10,85,86,0.08);
  border:1.5px solid rgba(10,85,86,0.16);
  transition:background 0.14s;
}
.ec__receipt-btn:hover { background:rgba(10,85,86,0.14); color:var(--ec-teal); text-decoration:none; }
.ec__no-receipt { font-size:11.5px; color:#b0cece; }

.ec__modal-ft {
  display:flex; align-items:center; justify-content:space-between;
  padding:14px 22px; border-top:1px solid var(--ec-border);
  background:rgba(10,85,86,0.025); flex-wrap:wrap; gap:10px;
}
.ec__modal-ft-total { font-size:13px; color:var(--ec-muted); }
.ec__modal-ft-total strong { font-family:'Sora',sans-serif; font-size:15px; font-weight:800; color:var(--ec-teal); margin-left:6px; }
.ec__modal-close-btn {
  padding:9px 22px; border-radius:11px; border:none; cursor:pointer;
  font-size:13px; font-family:'Sora',sans-serif; font-weight:700;
  color:var(--ec-muted); background:rgba(10,85,86,0.08);
  border:1.5px solid rgba(10,85,86,0.16); transition:background 0.15s;
}
.ec__modal-close-btn:hover { background:rgba(10,85,86,0.14); }

@media(max-width:600px){ .ec__search-input{width:130px;} }
`;

/* ─── Helpers ─── */
const formatCurrency = (amount) =>
  Number(amount || 0).toLocaleString("en-IN", { style:"currency", currency:"INR" });

function CustomPagination({ totalItems, itemsPerPage, currentPage, onPageChange }) {
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  if (totalItems === 0) return null;
  let start = Math.max(1, currentPage - 2), end = Math.min(totalPages, currentPage + 2);
  const pages = [];
  for (let i = start; i <= end; i++) pages.push(i);
  return (
    <div className="ec__pagination">
      <button className="ec__page-btn" onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1}>
        <FiChevronLeft size={12} /> Prev
      </button>
      {pages.map(p => (
        <button key={p} className={`ec__page-btn ${currentPage === p ? "ec__page-btn--active" : ""}`} onClick={() => onPageChange(p)}>{p}</button>
      ))}
      <button className="ec__page-btn" onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages}>
        Next <FiChevronRight size={12} />
      </button>
    </div>
  );
}

/* ─── Component ─── */
function ExpenseCategory() {
  const [categories, setCategories]         = useState([]);
  const [loading, setLoading]               = useState(true);
  const [searchTerm, setSearchTerm]         = useState("");
  const [sortBy, setSortBy]                 = useState({ key:"category", direction:"asc" });
  const [currentPage, setCurrentPage]       = useState(1);
  const [itemsPerPage, setItemsPerPage]     = useState(10);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showModal, setShowModal]           = useState(false);

  const token = localStorage.getItem("auth_token");

  /* ── fetch (unchanged) ── */
  useEffect(() => { fetchGroupedExpenses(); }, [token]);

  const fetchGroupedExpenses = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${baseurl}getExpensesGroupedByCategory`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) setCategories(res.data.data);
    } catch (error) {
      console.error("FETCH ERROR:", error);
    } finally {
      setLoading(false);
    }
  };

  /* ── process (unchanged) ── */
  const processedCategories = useMemo(() => {
    let list = [...categories];
    if (searchTerm.trim()) {
      const s = searchTerm.trim().toLowerCase();
      list = list.filter(c => c.category.toLowerCase().includes(s));
    }
    list.sort((a, b) => {
      const aVal = a[sortBy.key], bVal = b[sortBy.key];
      let cmp = typeof aVal === "number" ? aVal - bVal : String(aVal).localeCompare(String(bVal));
      return sortBy.direction === "asc" ? cmp : -cmp;
    });
    return list;
  }, [categories, searchTerm, sortBy]);

  const totalItems = processedCategories.length;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginated  = processedCategories.slice(startIndex, startIndex + itemsPerPage);

  const handleSort = (key) => setSortBy(prev => ({
    key, direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
  }));

  const SortIcon = ({ columnKey }) => {
    const isActive = sortBy.key === columnKey;
    const cls = `ec__sort-icon${isActive ? " ec__sort-icon--on" : ""}`;
    return isActive && sortBy.direction === "asc"
      ? <FiChevronsUp size={13} className={cls} />
      : <FiChevronsDown size={13} className={cls} />;
  };

  /* ── modal handler (unchanged) ── */
  const openVoucherModal = (categoryObj) => { setSelectedCategory(categoryObj); setShowModal(true); };

  /* ═══ RENDER ═══ */
  return (
    <>
      <style>{CSS}</style>
      <Topbar />
      <Sidebar />

      <main className="main-wrapper">
        <div className="ec__wrap">

          {/* ── Header ── */}
          <div className="ec__hd">
            <div className="ec__hd-left">
              <div className="ec__hd-icon"><FiLayers /></div>
              <div>
                <div className="ec__hd-title">Expense Analytics</div>
                <div className="ec__hd-sub">Analyze business spending grouped by category</div>
              </div>
            </div>
          </div>

          {/* ── Card ── */}
          <div className="ec__card">

            {/* Toolbar */}
            <div className="ec__toolbar">
              <div className="ec__toolbar-left">
                <span className="ec__show-lbl">Show</span>
                <select className="ec__select" value={itemsPerPage}
                  onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                </select>
              </div>
              <div className="ec__search">
                <FiSearch className="ec__search-icon" />
                <input className="ec__search-input" placeholder="Search categories…"
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} />
              </div>
            </div>

            {/* Table */}
            <div className="ec__table-wrap">
              {loading ? (
                <div className="ec__spinner" />
              ) : (
                <table className="ec__table">
                  <thead>
                    <tr>
                      <th onClick={() => handleSort("category")}>Category Name <SortIcon columnKey="category" /></th>
                      <th style={{ textAlign:"center" }} onClick={() => handleSort("total_count")}>Vouchers <SortIcon columnKey="total_count" /></th>
                      <th style={{ textAlign:"right" }} onClick={() => handleSort("total_amount")}>Total Spending <SortIcon columnKey="total_amount" /></th>
                      <th style={{ textAlign:"center" }}>Latest Entry</th>
                      <th style={{ textAlign:"center" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginated.length === 0 ? (
                      <tr><td colSpan="5">
                        <div className="ec__empty">
                          <div className="ec__empty-icon">🔍</div>
                          <div className="ec__empty-title">No categories found</div>
                          <div className="ec__empty-sub">Try adjusting your search term.</div>
                        </div>
                      </td></tr>
                    ) : paginated.map((cat, index) => (
                      <tr key={index}>
                        <td>
                          <div className="ec__cat-cell">
                            <div className="ec__cat-icon"><FiTag size={14} /></div>
                            <span className="ec__cat-name">{cat.category}</span>
                          </div>
                        </td>
                        <td style={{ textAlign:"center" }}>
                          <span className="ec__count-badge">
                            <FiActivity size={10} />{cat.total_count} items
                          </span>
                        </td>
                        <td style={{ textAlign:"right" }}>
                          <span className="ec__amount">{formatCurrency(cat.total_amount)}</span>
                        </td>
                        <td style={{ textAlign:"center" }}>
                          <span className="ec__latest">
                            {cat.rows && cat.rows.length > 0 ? cat.rows[0].expense_date : "N/A"}
                          </span>
                        </td>
                        <td style={{ textAlign:"center" }}>
                          <button className="ec__view-btn" onClick={() => openVoucherModal(cat)} title="View Vouchers">
                            <FiEye size={13} /> View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Footer */}
            <div className="ec__footer">
              <div className="ec__footer-info">
                Showing <strong>{totalItems === 0 ? 0 : startIndex + 1}</strong> to{" "}
                <strong>{Math.min(startIndex + itemsPerPage, totalItems)}</strong> of{" "}
                <strong>{totalItems}</strong> categories
              </div>
              <CustomPagination
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                currentPage={currentPage}
                onPageChange={setCurrentPage}
              />
            </div>
          </div>

        </div>
      </main>

      {/* ── VOUCHER MODAL (unchanged logic) ── */}
      {showModal && selectedCategory && (
        <div className="ec__overlay" onClick={(e) => { if (e.target === e.currentTarget) setShowModal(false); }}>
          <div className="ec__modal">

            {/* Header */}
            <div className="ec__modal-hd">
              <div className="ec__modal-hd-left">
                <div className="ec__modal-hd-icon"><FiFileText size={16} /></div>
                <div>
                  <div className="ec__modal-title">Vouchers — {selectedCategory.category}</div>
                  <div className="ec__modal-sub">{selectedCategory.rows?.length} entries in this category</div>
                </div>
              </div>
              <button className="ec__modal-close" onClick={() => setShowModal(false)}><FiX size={15} /></button>
            </div>

            {/* Body */}
            <div className="ec__modal-body">
              <table className="ec__modal-table">
                <thead>
                  <tr>
                    <th>Title &amp; Vendor</th>
                    <th style={{ textAlign:"center" }}>Date</th>
                    <th style={{ textAlign:"center" }}>Status</th>
                    <th style={{ textAlign:"right" }}>Amount</th>
                    <th style={{ textAlign:"center" }}>Receipt</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedCategory.rows.map((row) => (
                    <tr key={row.expense_uuid}>
                      <td>
                        <div className="ec__row-title">{row.title}</div>
                        <div className="ec__row-vendor">
                          <FiUser size={10} />{row.vendor || "—"}
                        </div>
                      </td>
                      <td>
                        <div className="ec__row-date">
                          <FiCalendar size={11} />{row.expense_date}
                        </div>
                      </td>
                      <td style={{ textAlign:"center" }}>
                        <span className={`ec__status-badge ${row.payment_status === "Paid" ? "ec__status-badge--paid" : "ec__status-badge--warning"}`}>
                          {row.payment_status}
                        </span>
                      </td>
                      <td>
                        <div className="ec__row-amount">{formatCurrency(row.amount)}</div>
                      </td>
                      <td style={{ textAlign:"center" }}>
                        {row.attachment_url ? (
                          <a href={`${imgurl}${row.attachment_url}`} target="_blank" rel="noreferrer" className="ec__receipt-btn">
                            <FiEye size={11} /> View
                          </a>
                        ) : (
                          <span className="ec__no-receipt">None</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Footer */}
            <div className="ec__modal-ft">
              <div className="ec__modal-ft-total">
                Total Category Spending:
                <strong>{formatCurrency(selectedCategory.total_amount)}</strong>
              </div>
              <button className="ec__modal-close-btn" onClick={() => setShowModal(false)}>Close</button>
            </div>

          </div>
        </div>
      )}
    </>
  );
}

export default ExpenseCategory;
